import React, { useState, useEffect } from "react";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Search,
  Pencil,
  Trash2,
  LogOut,
} from "lucide-react";
import "../Styles/dashgallery.css";
import Swal from "sweetalert2";


// Change this if your backend runs on a different host/port
const API_BASE_URL = "";



const TABS = [
  {
    key: "add",
    label: "Add Event",
    icon: "/assets/dbg_icon_1.png",
  },
  {
    key: "upload",
    label: "Upload Event",
    icon: "/assets/dbg_icon_2.png",
  },
  {
    key: "manage",
    label: "Manage Gallery",
    icon: "/assets/dbg_icon_3.png",
  },
];

const ITEMS_PER_PAGE = 100;

export default function DashGallery() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeTab, setActiveTab] = useState("add");
  const [query, setQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // ---- Upload Event Image tab state ----
 const [uploadEventId, setUploadEventId] = useState("");
  const [uploadFile, setUploadFile] = useState(null);

  // ---- simple "Edit / Add" modal — used ONLY on the Add Event tab ----
  // eventModal = null (closed) OR { mode: 'add' | 'edit', id: number|null, eventName: string }
  const [eventModal, setEventModal] = useState(null);

  // ---- original Manage Gallery edit modal state — used ONLY on the Manage Gallery tab ----
  const [editEvent, setEditEvent] = useState(null); // holds the event being edited, or null
  const [editEventName, setEditEventName] = useState("");
  const [editFile, setEditFile] = useState(null);

  // ---- Delete modal state ----
  const [deleteTarget, setDeleteTarget] = useState(null); // holds the event pending delete, or null

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/gallery-events`);
      if (!res.ok) throw new Error("Failed to load events");
      const data = await res.json();
      setEvents(data);
      setError(null);
    }  catch (err) {
  console.error(err);
  setEvents([]);
  setError("Failed to load events. Please try again.");
} finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [query, activeTab]);

  const filteredEvents = events.filter((e) =>
    e.eventName?.toLowerCase().includes(query.toLowerCase())
  );

  const totalPages = Math.max(1, Math.ceil(filteredEvents.length / ITEMS_PER_PAGE));
  const pageStart = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedEvents = filteredEvents.slice(pageStart, pageStart + ITEMS_PER_PAGE);

  // "Add New Event" button (Add Event tab only) opens the simple Edit/Add modal
  const handleAddNewEvent = () => {
    setEventModal({ mode: "add", id: null, eventName: "" });
  };

const handleUploadSubmit = async () => {
  if (!uploadEventId) {
    alert("Please select an event.");
    return;
  }
  if (!uploadFile) {
    alert("Please choose an image to upload.");
    return;
  }
  try {
    const formData = new FormData();
    formData.append("image", uploadFile);

    const res = await fetch(
      `${API_BASE_URL}/api/gallery-events/${uploadEventId}/images`,
      { method: "POST", body: formData }
    );
    if (!res.ok) throw new Error("Failed to upload image");

    await fetchEvents(); // refresh so the new image shows up everywhere
    setUploadFile(null);
    setUploadEventId("");
    Swal.fire({
      title: "Success!",
      text: "Image uploaded successfully.",
      icon: "success",
      timer: 1500,
      showConfirmButton: false,
    });
  } catch (err) {
    console.error(err);
    Swal.fire({ title: "Error", text: "Failed to upload image. Please try again.", icon: "error" });
  }
};

  // ---------------- simple Edit/Add modal logic (Add Event tab) ----------------
  const openSimpleEditModal = (event) => {
    setEventModal({ mode: "edit", id: event.id, eventName: event.eventName || "" });
  };

  const closeEventModal = () => {
    setEventModal(null);
  };

  const updateEventModalName = (value) => {
    setEventModal((prev) => (prev ? { ...prev, eventName: value } : prev));
  };

  const handleSaveEvent = async () => {
    if (!eventModal) return;

    if (!eventModal.eventName.trim()) {
      alert("Please enter an event name.");
      return;
    }

    try {
      if (eventModal.mode === "add") {
        const res = await fetch(`${API_BASE_URL}/api/gallery-events`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ eventName: eventModal.eventName }),
        });
        if (!res.ok) throw new Error("Failed to add event");
        const created = await res.json();
        setEvents((prev) => [...prev, created]);
      } else {
        const res = await fetch(`${API_BASE_URL}/api/gallery-events/${eventModal.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ eventName: eventModal.eventName }),
        });
        if (!res.ok) throw new Error("Failed to update event");
        const updated = await res.json();
        setEvents((prev) =>
          prev.map((e) => (e.id === eventModal.id ? { ...e, ...updated } : e))
        );
      }
      closeEventModal();
    } catch (err) {
      console.error(err);
      alert(
        eventModal.mode === "add"
          ? "Failed to add event. Please try again."
          : "Failed to update event. Please try again."
      );
    }
  };

  // ---------------- restored original Manage Gallery edit modal logic ----------------
  const openEditModal = (event) => {
    setEditEvent(event);
    setEditEventName(event.eventName || "");
    setEditFile(null);
  };

  const closeEditModal = () => {
    setEditEvent(null);
    setEditEventName("");
    setEditFile(null);
  };

  const handleUpdateGallery = async () => {
    if (!editEvent) return;
    try {
      const formData = new FormData();
      formData.append("eventName", editEventName);
      if (editFile) formData.append("image", editFile);
      const res = await fetch(`${API_BASE_URL}/api/gallery-events/${editEvent.id}`, {
        method: "PUT",
        body: formData,
      });
      if (!res.ok) throw new Error("Failed to update event");
      const updated = await res.json();
      setEvents((prev) =>
        prev.map((e) => (e.id === editEvent.id ? { ...e, ...updated } : e))
      );
      closeEditModal();
    } catch (err) {
      console.error(err);
      alert("Failed to update gallery. Please try again.");
    }
  };

  // ---------------- single Edit handler — routes to the correct modal based on active tab ----------------
  const handleEditClick = (event) => {
    if (activeTab === "manage") {
      openEditModal(event); // original image-based modal
    } else {
      openSimpleEditModal(event); // new simple "Edit / Add" modal
    }
  };

  // ---------------- Delete modal ----------------
  const openDeleteModal = (event) => setDeleteTarget(event);
  const closeDeleteModal = () => setDeleteTarget(null);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/gallery-events/${deleteTarget.id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete");
      setEvents((prev) => prev.filter((e) => e.id !== deleteTarget.id));
    } catch (err) {
      console.error(err);
      alert("Failed to delete event. Please try again.");
    } finally {
      closeDeleteModal();
    }
  };

  const showImageColumn = activeTab === "manage";
  const showPagination = activeTab === "manage";
  // CHANGED: Search box + Add New Event button should only show on the Add Event tab,
  // not on Manage Gallery.
  const showControls = activeTab !== "manage";

  return (
    <div className="dgl-app">
      {/* ---------------- CHANGED: Sidebar removed as requested ---------------- */}

      {/* ---------------- Main content ---------------- */}
      <main className="dgl-main">
        <header className="dgl-topbar">
          <h1>Gallery Management</h1>
          <button className="dgl-logout">
            <LogOut size={14} />
            Logout
          </button>
        </header>

        {/* Tab bar */}
        <div className="dgl-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              className={`dgl-tab${activeTab === tab.key ? " active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <img
                src={tab.icon}
                alt={tab.label}
                className="dgl-tab-icon"
              />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* ---------------- Add New Event / Manage Gallery (table view) ---------------- */}
        {(activeTab === "add" || activeTab === "manage") && (
          <section className="dgl-panel">
            {/* Controls row — CHANGED: hidden on Manage Gallery tab */}
            {showControls && (
              <div className="dgl-controls">
                <div className="dgl-search">
                  <Search size={14} />
                  <input
                    placeholder="Search..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </div>
                <button className="dgl-add-btn" onClick={handleAddNewEvent}>
                  <img
                      src="/assets/dbg_icon_6.png"
                      alt="Add"
                      className="dgl-add-icon"
                  />
                  Add New Event
              </button>
              </div>
            )}

            {/* Table */}
            <div className="dgl-table-wrap">
              {loading ? (
                <p className="dgl-state-msg">Loading events...</p>
              ) : error ? (
                <p className="dgl-state-msg dgl-state-msg--error">{error}</p>
              ) : (
                <table className="dgl-table">
                  <thead>
                    <tr>
                      <th className="dgl-sno-col">S.No</th>
                      {showImageColumn && <th className="dgl-image-col">Image</th>}
                      <th>Event Name</th>
                      <th className="dgl-action-col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedEvents.length === 0 ? (
                      <tr>
                        <td colSpan={showImageColumn ? 4 : 3} className="dgl-state-msg">
                          No events yet.
                        </td>
                      </tr>
                    ) : (
                      paginatedEvents.map((e, index) => (
                        <tr key={e.id}>
                          <td className="dgl-sno-col">{pageStart + index + 1}</td>
                          {showImageColumn && (
                            <td className="dgl-image-col">
                              <img
                                src={e.imageUrl || "/assets/gallery-sample.jpg"}
                                alt={e.eventName}
                                className="dgl-thumb"
                              />
                            </td>
                          )}
                          <td>{e.eventName}</td>
                          <td className="dgl-action-col">
                            <div className="dgl-row-actions">
                              <button
                              className="dgl-icon-btn dgl-icon-btn--edit"
                              onClick={() => handleEditClick(e)}
                            >
                              <img
                                src="/assets/dbg_icon_4.png"
                                alt="Edit"
                                className="dgl-action-icon"
                              />
                              Edit
                            </button>
                              <button
                            className="dgl-icon-btn dgl-icon-btn--delete"
                            onClick={() => openDeleteModal(e)}
                          >
                            <img
                              src="/assets/dbg_icon_5.png"
                              alt="Delete"
                              className="dgl-action-icon"
                            />
                            Delete
                          </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>

            {showPagination && !loading && !error && (
              <div className="dgl-pagination">
                <span>
                  Showing {filteredEvents.length === 0 ? 0 : pageStart + 1}-
                  {Math.min(pageStart + ITEMS_PER_PAGE, filteredEvents.length)} of{" "}
                  {filteredEvents.length} Members
                </span>
                <div className="dgl-page-controls">
                  <button
                    className="dgl-page-btn dgl-page-arrow"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft size={14} />
                  </button>
                  {Array.from({ length: Math.min(totalPages, 3) }, (_, i) => i + 1).map(
                    (p) => (
                      <button
                        key={p}
                        className={`dgl-page-btn${currentPage === p ? " active" : ""}`}
                        onClick={() => setCurrentPage(p)}
                      >
                        {p}
                      </button>
                    )
                  )}
                  {totalPages > 3 && <span className="dgl-page-ellipsis">...</span>}
                  {totalPages > 3 && (
                    <button
                      className={`dgl-page-btn${currentPage === totalPages ? " active" : ""}`}
                      onClick={() => setCurrentPage(totalPages)}
                    >
                      {totalPages}
                    </button>
                  )}
                  <button
                    className="dgl-page-btn dgl-page-arrow"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </section>
        )}

        {/* ---------------- Upload Event Image (card view) ---------------- */}
        {activeTab === "upload" && (
          <section className="dgl-panel dgl-panel--upload">
            <div className="dgl-upload-card">
              <h2>Upload Events Images</h2>

              <label className="dgl-field-label">Select category:</label>
<div className="dgl-select-wrap">
  <select
    value={uploadEventId}
    onChange={(e) => setUploadEventId(e.target.value)}
  >
    <option value="">-Select Event-</option>
    {events.map((ev) => (
      <option key={ev.id} value={ev.id}>
        {ev.eventName}
      </option>
    ))}
  </select>
  <ChevronDown size={14} className="dgl-select-chevron" />
</div>

              <label className="dgl-field-label">Select Image to Upload:</label>
              <label className="dgl-file-input">
                <span className="dgl-file-btn">Choose File</span>
                <span className="dgl-file-name">
                  {uploadFile ? uploadFile.name : "No file Choosen"}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                />
              </label>

              <button className="dgl-primary-btn" onClick={handleUploadSubmit}>
                Upload Image
              </button>
            </div>
          </section>
        )}
      </main>

      {/* ---------------- simple "Edit / Add" modal — Add Event tab only ---------------- */}
      {eventModal && (
        <div className="dgl-modal-overlay" onClick={closeEventModal}>
          <div className="dgl-modal-card dgl-event-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Edit / Add</h2>

            <label className="dgl-field-label">Event Name</label>
            <input
              type="text"
              className="dgl-event-input"
              value={eventModal.eventName}
              onChange={(e) => updateEventModalName(e.target.value)}
              autoFocus
            />

            <div className="dgl-event-modal-actions">
              <button className="dgl-btn-save" onClick={handleSaveEvent}>
                Save
              </button>
              <button className="dgl-btn-cancel-outline" onClick={closeEventModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- original image-based Edit Modal — Manage Gallery tab only ---------------- */}
      {editEvent && (
        <div className="dgl-modal-overlay" onClick={closeEditModal}>
          <div className="dgl-modal-card" onClick={(e) => e.stopPropagation()}>
            <h2>Manage Gallery</h2>

            <img
              src={editEvent.imageUrl || "/assets/gallery-sample.jpg"}
              alt={editEvent.eventName}
              className="dgl-modal-preview"
            />

            <label className="dgl-field-label">Select Event Name:</label>
            <div className="dgl-select-wrap">
              <select
                value={editEventName}
                onChange={(e) => setEditEventName(e.target.value)}
              >
                <option value="">-All-</option>
                {events.map((e) => (
                  <option key={e.id} value={e.eventName}>
                    {e.eventName}
                  </option>
                ))}
              </select>
              <ChevronDown size={14} className="dgl-select-chevron" />
            </div>

            <label className="dgl-field-label">Select Image to Upload:</label>
            <label className="dgl-file-input">
              <span className="dgl-file-btn">Choose File</span>
              <span className="dgl-file-name">
                {editFile ? editFile.name : "No file Choosen"}
              </span>
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => setEditFile(e.target.files?.[0] || null)}
              />
            </label>

            <button className="dgl-gallery-btn" onClick={handleUpdateGallery}>
              Update Gallery
            </button>
          </div>
        </div>
      )}

      {/* ---------------- Delete Modal ---------------- */}
      {deleteTarget && (
        <div className="dgl-modal-overlay" onClick={closeDeleteModal}>
          <div className="dgl-modal-card dgl-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dgl-delete-icon">
            <img
              src="/assets/dbg_icon_07.png"
              alt="Delete Warning"
              className="dgl-delete-icon-img"
            />
          </div>
            <h2>Delete Information</h2>
            <p>This Record will be deleted</p>
            <div className="dgl-delete-actions">
              <button className="dgl-btn-keep" onClick={closeDeleteModal}>
                No, Keep It
              </button>
              <button className="dgl-btn-delete" onClick={confirmDelete}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}